import { DragEvent, RefObject, useState } from "react"
import { definition, styles } from "@uesio/ui"

interface UploadAreaProps {
  accept?: string
  onUpload: (files: FileList | null) => void
  onClick?: () => void
  uploadLabelId?: string
  fileInputRef?: RefObject<HTMLInputElement | null>
  activeClassName?: string
}

const StyleDefaults = Object.freeze({
  root: [],
  fileinput: ["hidden"],
})

const UploadArea: definition.UtilityComponent<UploadAreaProps> = (props) => {
  const classes = styles.useUtilityStyleTokens(StyleDefaults, props)
  const [isDragActive, setIsDragActive] = useState(false)

  const { children, uploadLabelId, onUpload, accept, fileInputRef } = props

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
    onUpload(e.dataTransfer.files)
  }

  const onDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }

  const onDragEnter = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }

  const onDragLeave = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }

  const resetFile = () => {
    // Make sure the upload triggers after a delete or an upload as the browser won't trigger onChange if the same file is selected again.
    // This covers the situation where the file is deleted but also the situation where the same file based on name/path is selected
    // even though the physical file might have changed.
    if (fileInputRef?.current) {
      fileInputRef.current.value = ""
    }
  }

  return (
    <>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        className={styles.cx(
          classes.root,
          isDragActive && props.activeClassName,
        )}
        onClick={(e) => {
          if (props.onClick) {
            e.preventDefault()
            e.stopPropagation()
            props.onClick()
          }
        }}
      >
        {children}
      </div>
      <input
        className={classes.fileinput}
        type="file"
        accept={accept}
        onChange={(e) => {
          onUpload(e.target.files)
          resetFile()
        }}
        ref={fileInputRef}
        id={uploadLabelId}
      />
    </>
  )
}

export default UploadArea
